import React, { useEffect, useRef, useState } from 'react';
import './NativePullToRefresh.css';

/**
 * NativePullToRefresh
 *
 * Re-implements the browser's "swipe down at the top of the page to reload"
 * gesture for the Capacitor Android wrapper. The WebView strips the native
 * Chrome pull-to-refresh, so without this component there is no way to force
 * a reload short of killing and reopening the app.
 *
 * Behavior:
 *   - Only mounts on a native Capacitor platform.
 *   - Tracks touchstart only when the document scroll is at the very top.
 *   - Follows the finger with a small indicator (visual feedback only).
 *   - Once the drag exceeds the trigger threshold and the finger lifts,
 *     calls `window.location.reload()`.
 *   - Cancelled if the user scrolls up before lifting or if a multi-touch
 *     gesture (pinch / two-finger scroll) starts.
 *
 * The component renders only a small fixed-position spinner overlay when
 * the gesture is in progress; otherwise it renders nothing.
 */

const TRIGGER_DISTANCE_PX = 80;     // how far the user must pull
const MAX_PULL_PX = 140;             // cap on indicator translation
const PULL_RESISTANCE = 0.55;       // damp the indicator so it feels native
const INDICATOR_REVEAL_OFFSET_PX = 32;

const isNativePlatform = () => (
  typeof window !== 'undefined' &&
  !!window.Capacitor &&
  typeof window.Capacitor.isNativePlatform === 'function' &&
  window.Capacitor.isNativePlatform()
);

const isAtTop = () => {
  const scrollTop = window.scrollY
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || 0;
  return scrollTop <= 0;
};

const NativePullToRefresh = () => {
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  // Refs so the touch handlers stay stable but still see current values
  const startYRef = useRef(null);
  const trackingRef = useRef(false);
  const triggeredRef = useRef(false);

  useEffect(() => {
    if (!isNativePlatform()) return undefined;

    const reset = () => {
      startYRef.current = null;
      trackingRef.current = false;
      triggeredRef.current = false;
      setPullDistance(0);
    };

    const onTouchStart = (e) => {
      if (refreshing) return;
      if (e.touches.length !== 1) {
        trackingRef.current = false;
        return;
      }
      // Don't intercept gestures inside a modal — the page scroll is locked
      // at the top while a modal is open, so otherwise every drag-down inside
      // the modal's own scroll container would trigger a reload.
      if (document.body.classList.contains('modal-open')) {
        trackingRef.current = false;
        return;
      }
      // Only start tracking if we're at the very top of the page.
      if (!isAtTop()) {
        trackingRef.current = false;
        return;
      }
      startYRef.current = e.touches[0].clientY;
      trackingRef.current = true;
      triggeredRef.current = false;
    };

    const onTouchMove = (e) => {
      if (!trackingRef.current || startYRef.current == null) return;
      if (e.touches.length !== 1) {
        reset();
        return;
      }
      const delta = e.touches[0].clientY - startYRef.current;
      if (delta <= 0) {
        // User is scrolling up (or not moving down) — bail.
        if (pullDistance !== 0) setPullDistance(0);
        return;
      }
      // Apply resistance and cap.
      const visual = Math.min(MAX_PULL_PX, delta * PULL_RESISTANCE);
      setPullDistance(visual);
      triggeredRef.current = visual >= TRIGGER_DISTANCE_PX;
      // Prevent the page from rubber-banding or selecting text while we drag.
      if (e.cancelable) e.preventDefault();
    };

    const onTouchEnd = () => {
      if (!trackingRef.current) return;
      if (triggeredRef.current) {
        setRefreshing(true);
        // Tiny delay so the spinner is visible before the reload nukes everything.
        window.setTimeout(() => {
          window.location.reload();
        }, 120);
      } else {
        reset();
      }
    };

    const onTouchCancel = () => reset();

    // touchmove must be non-passive so we can preventDefault on the pull.
    document.addEventListener('touchstart', onTouchStart, { passive: true });
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', onTouchEnd, { passive: true });
    document.addEventListener('touchcancel', onTouchCancel, { passive: true });

    return () => {
      document.removeEventListener('touchstart', onTouchStart);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('touchcancel', onTouchCancel);
    };
  }, [refreshing, pullDistance]);

  if (!isNativePlatform()) return null;
  if (pullDistance === 0 && !refreshing) return null;

  const armed = pullDistance >= TRIGGER_DISTANCE_PX || refreshing;
  const translateY = refreshing ? TRIGGER_DISTANCE_PX : pullDistance;
  const rotation = refreshing ? 0 : Math.min(360, (pullDistance / TRIGGER_DISTANCE_PX) * 270);
  const indicatorTranslateY = translateY - INDICATOR_REVEAL_OFFSET_PX;

  return (
    <div
      aria-hidden="true"
      className={`native-pull-refresh${armed ? ' is-armed' : ''}${refreshing ? ' is-refreshing' : ''}`}
      style={{
        '--ptr-translate-y': `${indicatorTranslateY}px`,
        '--ptr-rotation': `${rotation}deg`
      }}
    >
      <div className="native-pull-refresh__surface">
        <div className="native-pull-refresh__spinner" />
      </div>
    </div>
  );
};

export default NativePullToRefresh;
